import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableFootClass,
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../components/DataTablePanel';
import { SortableTh, sortItems, useTableSort } from '../components/SortableTh';
import { Callout, Card, ListEmptyState, LoadingSpinner } from '../components/ui';
import { ApiError, api } from '../services/api';
import { t } from '../i18n';
import { MoneyTotals } from '../types/customer';
import { DashboardData } from '../types/dashboard';
import { AgingBucket } from '../types/reports';
import { formatDate, formatMoney } from '../utils/format';
import { sumMoney } from '../utils/exportTabularList';

const totalsCellClass = 'sticky bottom-0 z-10 bg-surface-muted px-4 py-2';
const totalsNumClass = `${totalsCellClass} text-right tabular-nums`;

function daysSinceLastPayment(value: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  const startToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startPayment = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  return Math.max(
    0,
    Math.round((startToday.getTime() - startPayment.getTime()) / 86400000),
  );
}

const BUCKETS: AgingBucket[] = [
  'notDue',
  'd1_30',
  'd31_60',
  'd61_90',
  'd90plus',
];

const debtorSortGetters: Record<
  string,
  (
    row: DashboardData['topDebtors'][number],
  ) => string | number | boolean | null | undefined
> = {
  customerName: (r) => r.customerName,
  officeName: (r) => r.officeName,
  assignedUserName: (r) => r.assignedUserName,
  outstandingInvoice: (r) => r.outstanding.invoice,
  outstandingCash: (r) => r.outstanding.cash,
  outstandingTotal: (r) => r.outstanding.total,
  lastPaymentDate: (r) => r.lastPaymentDate,
  daysSinceLastPayment: (r) => daysSinceLastPayment(r.lastPaymentDate),
  openInvoiceCount: (r) => r.openInvoiceCount,
};

export function DashboardPage() {
  const labels = t().dashboard;
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('outstandingTotal', 'desc');
  const displayDebtors = useMemo(
    () =>
      data ? sortItems(data.topDebtors, sort, debtorSortGetters) : [],
    [data, sort],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getDashboard();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <LoadingSpinner message={t().common.loading} />;
  }

  if (!data) {
    return <Callout variant="danger">{error || t().common.error}</Callout>;
  }

  const sections = data.sections ?? {
    monthly: true,
    balances: true,
    aging: true,
    topDebtors: true,
    shipment: true,
    companyExpenses: false,
  };
  const hasAnySection =
    sections.monthly ||
    sections.balances ||
    sections.aging ||
    sections.topDebtors ||
    sections.shipment ||
    sections.companyExpenses;

  const monthLabel = `${t().common.months[data.month - 1]} ${data.year}`;
  const moneyCards: Array<{
    key: string;
    title: string;
    totals: MoneyTotals;
    href: string;
    emphasize?: boolean;
  }> = [];

  if (sections.monthly) {
    moneyCards.push(
      {
        key: 'charges',
        title: `${labels.monthCharges} · ${monthLabel}`,
        totals: data.monthly.charges,
        href: '/reports/monthly-summary',
      },
      {
        key: 'payments',
        title: `${labels.monthPayments} · ${monthLabel}`,
        totals: data.monthly.payments,
        href: '/reports/monthly-summary',
      },
    );
  }
  if (sections.balances) {
    moneyCards.push({
      key: 'total',
      title: labels.totalBalance,
      totals: data.totalOutstanding,
      href: '/reports/customer-balances',
    });
  }
  if (sections.aging) {
    moneyCards.push({
      key: 'overdue',
      title: labels.overdueBalance,
      totals: data.overdueOutstanding,
      href: '/reports/aging',
      emphasize: true,
    });
  }

  return (
    <PageShell>
      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      {!hasAnySection ? (
        <Callout variant="info" className="shrink-0">
          {labels.noActiveSections}
        </Callout>
      ) : null}

      {moneyCards.length > 0 ? (
        <div className="grid shrink-0 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {moneyCards.map((card) => (
            <MoneyCard
              key={card.key}
              title={card.title}
              totals={card.totals}
              href={card.href}
              emphasize={card.emphasize}
            />
          ))}
        </div>
      ) : null}

      {sections.shipment ? (
        <div className="grid shrink-0 gap-4 md:grid-cols-3">
          <StatCard
            title={labels.shipmentOpenCount}
            primary={String(data.shipmentOpenCount)}
            href="/reports/shipment-open-files"
          />
          <StatCard
            title={labels.shipmentOpenBillable}
            primary={formatMoney(data.shipmentOpenBillable.gross)}
            secondary={`${t().reports.expenseNet} ${formatMoney(data.shipmentOpenBillable.net)} · ${t().reports.expenseVat} ${formatMoney(data.shipmentOpenBillable.vat)}`}
            href="/reports/shipment-open-files"
          />
          <StatCard
            title={`${labels.shipmentInvoicedMonth} · ${monthLabel}`}
            primary={String(data.shipmentInvoicedMonth.count)}
            secondary={`${t().reports.expenseGross} ${formatMoney(data.shipmentInvoicedMonth.gross)}`}
            href="/reports/shipment-monthly-summary"
          />
        </div>
      ) : null}

      {sections.companyExpenses ? (
        <div className="grid shrink-0 gap-4 md:grid-cols-2">
          <StatCard
            title={labels.companyExpenseOverdue}
            primary={formatMoney(data.companyExpenseOverdue)}
            href="/company-expenses/payables"
          />
          <StatCard
            title={labels.companyExpenseDue7Days}
            primary={formatMoney(data.companyExpenseDue7Days)}
            href="/company-expenses/payables"
          />
        </div>
      ) : null}

      {sections.aging ? (
        <Card className="shrink-0 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink">{labels.agingTitle}</h2>
            <Link
              to="/reports/aging"
              className="text-sm text-brand hover:underline"
            >
              {labels.viewAging}
            </Link>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {BUCKETS.map((bucket) => (
              <Card key={bucket} className="p-3 text-sm" variant="outlined">
                <div className="text-xs text-ink-faint">
                  {t().reports.buckets[bucket]}
                </div>
                <div className="mt-2 text-lg font-semibold tabular-nums">
                  {formatMoney(data.aging.buckets[bucket].total)}
                </div>
              </Card>
            ))}
            <Card className="border-brand/30 bg-brand-soft p-3 text-sm">
              <div className="text-xs text-brand">{labels.totalBalance}</div>
              <div className="mt-2 text-lg font-semibold tabular-nums text-brand">
                {formatMoney(data.aging.total.total)}
              </div>
            </Card>
          </div>
        </Card>
      ) : null}

      {sections.topDebtors ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex shrink-0 items-center justify-between px-1">
            <h2 className="text-sm font-semibold text-ink">{labels.topDebtors}</h2>
            <Link
              to="/reports/customer-balances"
              className="text-sm text-brand hover:underline"
            >
              {labels.viewReports}
            </Link>
          </div>
          <DataTablePanel>
            {data.topDebtors.length === 0 ? (
              <ListEmptyState title={t().common.empty} />
            ) : (
              <table className="min-w-full text-sm">
                <thead className={dataTableHeadClass}>
                  <tr>
                    <SortableTh
                      sortKey="customerName"
                      sort={sort}
                      onSort={toggleSort}
                    >
                      {t().common.customer}
                    </SortableTh>
                    <SortableTh
                      sortKey="officeName"
                      sort={sort}
                      onSort={toggleSort}
                    >
                      {t().nav.office}
                    </SortableTh>
                    <SortableTh
                      sortKey="assignedUserName"
                      sort={sort}
                      onSort={toggleSort}
                    >
                      {t().customers.assignedUser}
                    </SortableTh>
                    <SortableTh
                      sortKey="outstandingInvoice"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {labels.balanceInvoice}
                    </SortableTh>
                    <SortableTh
                      sortKey="outstandingCash"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {labels.balanceCash}
                    </SortableTh>
                    <SortableTh
                      sortKey="outstandingTotal"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {labels.balanceTotal}
                    </SortableTh>
                    <SortableTh
                      sortKey="lastPaymentDate"
                      sort={sort}
                      onSort={toggleSort}
                    >
                      {labels.lastPaymentDate}
                    </SortableTh>
                    <SortableTh
                      sortKey="daysSinceLastPayment"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {labels.daysSinceLastPayment}
                    </SortableTh>
                    <SortableTh
                      sortKey="openInvoiceCount"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {labels.openInvoices}
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {displayDebtors.map((row) => (
                    <tr key={row.customerId} className="border-t border-line">
                      <td className="px-4 py-2">
                        <Link
                          to={`/customers/${row.customerId}`}
                          className="text-brand hover:underline"
                        >
                          {row.customerName}
                        </Link>
                      </td>
                      <td className="px-4 py-2">{row.officeName || '—'}</td>
                      <td className="px-4 py-2">{row.assignedUserName || '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatMoney(row.outstanding.invoice)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatMoney(row.outstanding.cash)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">
                        {formatMoney(row.outstanding.total)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2">
                        {row.lastPaymentDate
                          ? formatDate(row.lastPaymentDate)
                          : '—'}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {daysSinceLastPayment(row.lastPaymentDate) ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {row.openInvoiceCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className={dataTableFootClass}>
                  <tr>
                    <td className={totalsCellClass}>{t().common.total}</td>
                    <td className={totalsCellClass} />
                    <td className={totalsCellClass} />
                    <td className={totalsNumClass}>
                      {formatMoney(
                        sumMoney(
                          data.topDebtors.map((row) => row.outstanding.invoice),
                        ),
                      )}
                    </td>
                    <td className={totalsNumClass}>
                      {formatMoney(
                        sumMoney(
                          data.topDebtors.map((row) => row.outstanding.cash),
                        ),
                      )}
                    </td>
                    <td className={totalsNumClass}>
                      {formatMoney(
                        sumMoney(
                          data.topDebtors.map((row) => row.outstanding.total),
                        ),
                      )}
                    </td>
                    <td className={totalsCellClass} />
                    <td className={totalsCellClass} />
                    <td className={totalsNumClass}>
                      {data.topDebtors.reduce(
                        (sum, row) => sum + row.openInvoiceCount,
                        0,
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </DataTablePanel>
        </div>
      ) : null}
    </PageShell>
  );
}

function MoneyCard({
  title,
  totals,
  href,
  emphasize = false,
}: {
  title: string;
  totals: MoneyTotals;
  href: string;
  emphasize?: boolean;
}) {
  return (
    <Link to={href} className="block transition-shadow hover:shadow-bf-md">
      <Card
        className={[
          'h-full p-4',
          emphasize
            ? 'border-red-300/70 bg-red-50/50 dark:border-red-500/35 dark:bg-red-950/35'
            : 'hover:border-brand/25',
        ].join(' ')}
      >
        <div className="text-xs font-medium uppercase tracking-wider text-ink-faint">
          {title}
        </div>
        <div className="mt-3 space-y-1.5 text-sm">
          <div className="flex justify-between text-ink-muted">
            <span>{t().financial.invoice}</span>
            <span className="tabular-nums text-ink">
              {formatMoney(totals.invoice)}
            </span>
          </div>
          <div className="flex justify-between text-ink-muted">
            <span>{t().financial.cash}</span>
            <span className="tabular-nums text-ink">
              {formatMoney(totals.cash)}
            </span>
          </div>
          <div className="flex justify-between border-t border-line pt-1.5 text-base font-semibold text-ink">
            <span>{t().financial.grandTotal}</span>
            <span className="tabular-nums">{formatMoney(totals.total)}</span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function StatCard({
  title,
  primary,
  secondary,
  href,
}: {
  title: string;
  primary: string;
  secondary?: string;
  href: string;
}) {
  return (
    <Link to={href} className="block transition-shadow hover:shadow-bf-md">
      <Card className="h-full p-4 hover:border-brand/25">
        <div className="text-xs font-medium uppercase tracking-wider text-ink-faint">
          {title}
        </div>
        <div className="mt-3 text-2xl font-semibold tabular-nums text-ink">
          {primary}
        </div>
        {secondary ? (
          <div className="mt-1.5 text-xs text-ink-muted">{secondary}</div>
        ) : null}
      </Card>
    </Link>
  );
}
